import { CourseStatus, type CourseSkillLevel } from '@/lib/constants';
import { handleSupabaseList, supabase } from '@/lib/supabase-unified';
import type { CourseThumbnailItem } from '@/types';
import {
  fetchUserCourseProgress,
  type CourseThumbnailsCategory,
} from './optimizedCourse';

const PURCHASED_CATEGORY: Pick<CourseThumbnailsCategory, 'id' | 'title' | 'slug'> = {
  id: -1,
  title: 'Purchased Courses',
  slug: 'purchased-courses',
};

type PurchasedCourseRow = {
  id: number;
  title: string;
  slug: string;
  thumbnail: string | null;
  status: number;
  lesson_slug: string | null;
  summary: string | null;
  skill_level: CourseSkillLevel | null;
  instructor_names: string[] | null;
  course_type: number | null;
  redirect_url: string | null;
};

/**
 * Courses linked to the user's WC plans (`user_memberships` → `membership_courses`),
 * plus per-lesson (companion) grants (`membership_lessons`), one card per lesson.
 * Uses RPC `get_purchased_courses_for_user_v3` (all membership courses, any status,
 * plus the catalog fields the "See all" grid/list cards render).
 * Navigation: status 3 → Josephine (lesson grants deep-link to the lesson);
 * any other status → WordPress.
 */
export async function fetchPurchasedCoursesCategory(
  userId: string | null,
  minStatus: number = CourseStatus.Published,
): Promise<CourseThumbnailsCategory> {
  if (!userId) {
    return { ...PURCHASED_CATEGORY, courses: [] };
  }

  const rows = await handleSupabaseList<PurchasedCourseRow>(
    supabase.rpc('get_purchased_courses_for_user_v3', {
      p_user_id: userId,
      p_min_status: minStatus,
    }) as never,
  );

  const courseIds = rows.map((r) => r.id);
  const progressMap = await fetchUserCourseProgress(userId, courseIds);

  const courses: CourseThumbnailItem[] = rows.map((course) => {
    const progress = progressMap[course.id];
    return {
      id: course.id,
      title: course.title,
      slug: course.slug,
      thumbnail: course.thumbnail ?? null,
      status: course.status,
      lessonSlug: course.lesson_slug ?? null,
      summary: course.summary ?? null,
      skillLevel: course.skill_level ?? null,
      instructorNames: course.instructor_names ?? null,
      courseType: course.course_type ?? null,
      redirectUrl: course.redirect_url ?? null,
      progressPercentage:
        progress !== undefined ? Math.round(progress * 100) : 0,
    };
  });

  return {
    ...PURCHASED_CATEGORY,
    courses,
  };
}
